import { ApiError, isApiError, errorMessageOf, validationError } from './apiErrors';

/** Plain wording for the procurement API's machine codes, so a person reads a sentence, never `over_receipt`. */
const BY_CODE: Record<string, string> = {
  over_receipt: 'You cannot receive more than is still remaining on a line. Check the quantities and try again.',
  waybill_required: 'Add a waybill to a linked shipment before moving the order into transit or later.',
  illegal_transition: 'That status change is not allowed from where this record is now.',
  duplicate_marketplace_order: 'An order with this marketplace order number already exists in this project.',
  line_has_receipts: 'This line already has goods received against it, so it cannot be removed.',
  shipment_not_arrived: 'This shipment has not arrived yet, so its goods cannot be confirmed as received.',
  already_confirmed: 'Part of this has already been confirmed against the shipment. Reload to see what is left.',
  shortfall_reason_required: 'Say why a line is short before confirming.',
  stock_item_required: 'Choose a stock item for each line that goes into stock, or create a new one.',
  role_forbidden: 'Only a planner or an owner can confirm a receipt.',
  milestone_order: 'The order\'s dates do not allow it to be marked received yet. Check its earlier steps.',
  negative_stock: 'That would take the stock below zero. Check the quantity and try again.',
  currency_required: 'Choose a currency before saving: amounts need one.',
  agent_in_use: 'This agent is used by an order or shipment. Switch it off instead of deleting it.',
};

/** The sentence to show for a failed procurement call. */
export function procurementErrorMessage(err: unknown, fallback: string): string {
  if (isApiError(err) && err.code && BY_CODE[err.code]) return BY_CODE[err.code];
  return errorMessageOf(err, fallback);
}

/**
 * The failure of a save as an ApiError whose message is the plain-language
 * procurement wording, with the status and field errors kept, so the shared
 * `useFormSaveEvent` shows it (and any field errors) the same way every other
 * form page does.
 */
export function toSaveError(err: unknown, fallback: string): ApiError {
  const message = procurementErrorMessage(err, fallback);
  if (isApiError(err)) return new ApiError(message, { status: err.status, fieldErrors: err.fieldErrors, code: err.code, body: err.body });
  return new ApiError(message);
}

export { validationError };
